import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, map } from 'rxjs';
import { PensionContributionEmployee, PensionSubmissionError } from './pension-contribution-employee.model';
import { PensionContributionApiResponse } from './pension-contribution.dto';
import { PensionEnrollmentRecord } from './pension-enrollment-record.model';
import { environment } from '@env/environment';
import { LoggingService } from '@logging/logging.service';

@Injectable()
export class PensionContributionsDataProvider {
  private readonly apiRoot = `${environment.apiPaths.pensions}`;

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggingService
  ) {}

  getSubmissionContributions$(closedPeriodId: number): Observable<PensionContributionEmployee[]> {
    return this.http.get<PensionContributionApiResponse[]>(`${this.apiRoot}/submission/${closedPeriodId}/contributions`).pipe(
      map((contributions) => contributions.map((contribution) => this.transformContribution(contribution))),
      catchError((error) => {
        this.logger.error('Failed to fetch pension contributions', error);
        return of([]);
      })
    );
  }

  private transformContribution(apiResponse: PensionContributionApiResponse): PensionContributionEmployee {
    return {
      employeeId: apiResponse.employeeId,
      employmentId: apiResponse.employmentId,
      firstName: apiResponse.firstName ?? '',
      surname: apiResponse.surname ?? '',
      errors: apiResponse.errors,
      periodEarnings: apiResponse.periodEarnings,
      employeeContribution: apiResponse.employeeContribution,
      employerContribution: apiResponse.employerContribution,
      total: apiResponse.total,
      pensionGroup: apiResponse.pensionGroup
    };
  }

  getSubmissionSubmisssionsErrors$(closedPeriodId: number): Observable<PensionSubmissionError[]> {
    return this.http.get<PensionSubmissionError[]>(`${this.apiRoot}/submission/${closedPeriodId}/closed-period-errors`).pipe(
      catchError((error) => {
        this.logger.error('Failed to fetch submission errors', error);
        return of([]);
      })
    );
  }

  getSubmissionPaymentErrors$(closedPeriodId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiRoot}/submission/${closedPeriodId}/payment-errors`).pipe(
      catchError((error) => {
        this.logger.error('Failed to fetch submission errors', error);
        return of([]);
      })
    );
  }

  getSubmissionEnrollments$(closedPeriodId: number): Observable<PensionEnrollmentRecord[]> {
    return this.http.get<PensionEnrollmentRecord[]>(`${this.apiRoot}/submission/${closedPeriodId}/enrollments`).pipe(
      catchError((error) => {
        this.logger.error('Failed to fetch enrollment data', error);
        return of([]);
      })
    );
  }
}
